import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, MapPin, AlertTriangle, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function SSFacilityMappingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
  const [facilities, setFacilities] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [ssOptions, setSsOptions] = useState([]);
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState({});
  const [cutoff, setCutoff] = useState(null);
  const [cutoffInput, setCutoffInput] = useState("");
  const [cutoffSaving, setCutoffSaving] = useState(false);

  const load = () => {
    api.get("/ss-pulse-sync/facilities").then(r => setFacilities(r.data)).catch(() => {});
    api.get("/ss-pulse-sync/facilities/unmapped").then(r => setUnmapped(r.data)).catch(() => {});
    api.get("/ss-pulse-sync/ss-options").then(r => setSsOptions(r.data)).catch(() => {});
    if (isAdmin) api.get("/ss-pulse-sync/cutoff").then(r => { setCutoff(r.data.cutoff_date); setCutoffInput(r.data.cutoff_date || ""); }).catch(() => {});
  };

  useEffect(load, []);

  const handleMap = async (fac) => {
    const key = fac.facility_id || fac.facility_name;
    const mapped_to = selections[key];
    if (!mapped_to) { toast.error("Select an SS or Skippi Direct"); return; }
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.post("/ss-pulse-sync/facilities/map", { facility_id: fac.facility_id, facility_name: fac.facility_name, city: fac.city, mapped_to });
      toast.success(`${fac.facility_name} mapped`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(s => ({ ...s, [key]: false }));
  };

  return (
    <div data-testid="ss-facility-mapping-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/ss-inventory")}><ArrowLeft size={16} /></Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Facility → SS Mapping</h1>
          <p className="text-sm text-zinc-500">Map each Blinkit facility to the Super Stockist that delivers to it, or to Skippi Direct.</p>
        </div>
      </div>

      {/* Cutoff Date Panel — Admin only */}
      {isAdmin && (
        <div className="mb-6 border border-zinc-200 rounded-sm p-4 bg-zinc-50" data-testid="cutoff-panel">
          <h2 className="text-sm font-semibold text-zinc-700 mb-1">GRN Deduction Start Date (Cutoff)</h2>
          <p className="text-[10px] text-zinc-500 mb-3">SS stock is only deducted for platform GRNs dated on or after this date. Set this to your go-live date, on or after the day physical stock is reset.</p>
          <div className="flex items-center gap-3">
            <div>
              <Label className="text-[10px] text-zinc-500">Current: <span className="font-mono font-semibold text-zinc-700">{cutoff || "Not set"}</span></Label>
            </div>
            <Input type="date" value={cutoffInput} onChange={e => setCutoffInput(e.target.value)} className="w-44 h-8 text-xs rounded-sm" data-testid="cutoff-input" />
            <Button size="sm" className="h-8 text-xs rounded-sm" disabled={cutoffSaving} data-testid="cutoff-save-btn" onClick={async () => {
              setCutoffSaving(true);
              try {
                const { data } = await api.post("/ss-pulse-sync/cutoff", { cutoff_date: cutoffInput });
                setCutoff(data.cutoff_date);
                toast.success(`Cutoff saved: ${data.cutoff_date || "cleared"}`);
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              setCutoffSaving(false);
            }}>{cutoffSaving ? "..." : "Save"}</Button>
          </div>
        </div>
      )}

      {/* Unmapped */}
      {unmapped.length > 0 && (
        <div className="mb-6 border border-amber-200 rounded-sm p-4 bg-amber-50" data-testid="unmapped-facilities">
          <h2 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2"><AlertTriangle size={14} />Unmapped Facilities — Please Assign ({unmapped.length})</h2>
          {unmapped.map(f => {
            const key = f.facility_id || f.facility_name;
            return (
              <div key={key} className="flex items-center gap-3 py-2 border-b border-amber-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{f.facility_name}</p>
                  <p className="text-[10px] text-zinc-500">{f.facility_id ? `ID: ${f.facility_id}` : ""} {f.city ? `· ${f.city}` : ""} · {f.po_count} POs</p>
                </div>
                <Select value={selections[key] || "none"} onValueChange={v => setSelections(s => ({ ...s, [key]: v === "none" ? "" : v }))}>
                  <SelectTrigger className="w-56 h-8 text-xs rounded-sm"><SelectValue placeholder="Select SS..." /></SelectTrigger>
                  <SelectContent>{[{ id: "none", name: "-- Select --" }, ...ssOptions].map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="h-7 text-[10px] rounded-sm" disabled={!selections[key] || saving[key]} onClick={() => handleMap(f)} data-testid={`map-${key}`}><Check size={12} className="mr-1" />{saving[key] ? "..." : "Save"}</Button>
              </div>
            );
          })}
        </div>
      )}

      {/* All Facilities */}
      <div className="border border-zinc-200 rounded-sm" data-testid="all-facilities">
        <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200"><h2 className="text-sm font-semibold text-zinc-700">All Facility Mappings ({facilities.length})</h2></div>
        <table className="data-table w-full">
          <thead><tr><th>Facility Name</th><th>Facility ID</th><th>City</th><th className="num">POs</th><th>Mapped To</th><th></th></tr></thead>
          <tbody>
            {facilities.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-zinc-400 text-xs">No Blinkit facilities found. Upload a scorecard first.</td></tr> : facilities.map(f => {
              const key = f.facility_id || f.facility_name;
              const isMapped = !!f.mapped_to;
              return (
                <tr key={key}>
                  <td className="text-xs font-medium">{f.facility_name}</td>
                  <td className="text-xs font-mono text-zinc-500">{f.facility_id || "—"}</td>
                  <td className="text-xs text-zinc-500">{f.city || "—"}</td>
                  <td className="num font-mono text-xs">{f.po_count}</td>
                  <td>
                    {isMapped ? (
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${f.mapped_to === "DIRECT_NO_SS" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{f.mapped_to_name}</span>
                    ) : (
                      <Select value={selections[key] || "none"} onValueChange={v => setSelections(s => ({ ...s, [key]: v === "none" ? "" : v }))}>
                        <SelectTrigger className="w-48 h-7 text-[10px] rounded-sm"><SelectValue placeholder="Assign..." /></SelectTrigger>
                        <SelectContent>{[{ id: "none", name: "-- Select --" }, ...ssOptions].map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </td>
                  <td>
                    {!isMapped && selections[key] && <Button size="sm" className="h-6 text-[10px] rounded-sm" disabled={saving[key]} onClick={() => handleMap(f)}>{saving[key] ? "..." : "Save"}</Button>}
                    {isMapped && <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { setSelections(s => ({ ...s, [key]: "" })); /* Allow reassign by clearing mapped status locally */ const updated = facilities.map(x => (x.facility_id || x.facility_name) === key ? { ...x, mapped_to: "", mapped_to_name: "UNMAPPED" } : x); setFacilities(updated); }}>Reassign</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
